import clsx from 'clsx';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, queryKeys } from '../services/endpoints.js';
import { haptics } from '../services/telegram.js';
import { FitText } from '../components/FitText.js';
import { useT, type CatalogKey } from '../i18n/index.js';

interface NavItem {
  to: string;
  tab: string;
  labelKey: CatalogKey;
}

/**
 * Five destinations, on the bar the artwork paints.
 *
 * The bar is no longer drawn from primitives: it is the one the Profile render
 * paints at its foot, cut by tools/slice_nav_bar.py into a plate and five
 * icons, and placed by styles/nav-artwork.css. So there are no Icon components
 * here any more — each item names its tab and the stylesheet knows which
 * picture that is. The words were painted in English; they are taken off the
 * plate (tools/blank_lettering.py) and written here in the reader's language.
 *
 * The plate is plain rock with no cell of its own, so the order is free: the
 * icon and its word travel together with the item. It reads home, tasks,
 * mining, wallet, profile.
 */
const ITEMS: readonly NavItem[] = [
  { to: '/', tab: 'home', labelKey: 'nav.home' },
  { to: '/tasks', tab: 'tasks', labelKey: 'nav.tasks' },
  { to: '/mining', tab: 'mining', labelKey: 'nav.mining' },
  { to: '/wallet', tab: 'wallet', labelKey: 'nav.wallet' },
  { to: '/profile', tab: 'profile', labelKey: 'nav.profile' },
] as const;

export function BottomNav() {
  const { t } = useT();
  const { data } = useQuery({ queryKey: queryKeys.mine, queryFn: api.mine.get, staleTime: 5_000 });
  const { data: tasks } = useQuery({ queryKey: queryKeys.tasks, queryFn: api.tasks.list, staleTime: 30_000 });

  const claimable = (data?.summary.pendingClaimMpxc ?? 0) > 0;
  const tasksPending = tasks ? tasks.completedCount < tasks.totalCount : false;

  return (
    <nav className="bottom-nav" aria-label={t('nav.main')}>
      {ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          data-tab={item.tab}
          className={({ isActive }) => clsx('bottom-nav__item', isActive && 'is-active')}
          onClick={() => haptics.select()}
        >
          <span className="bottom-nav__icon" aria-hidden />
          <FitText className="bottom-nav__label">{t(item.labelKey)}</FitText>
          {/* A dot only where there is something genuinely waiting. */}
          {item.to === '/mining' && claimable ? <span className="bottom-nav__dot" aria-hidden /> : null}
          {item.to === '/tasks' && tasksPending ? <span className="bottom-nav__dot" aria-hidden /> : null}
        </NavLink>
      ))}
    </nav>
  );
}
